import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Bell, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import useFormatter from "@/hooks/useFormatter";
import useConfirm from "@/hooks/useConfirm";
import { preferencesKeys } from "@/query-keys";
import { services } from "@/services";
import TelegramReportDetail from "../_components/TelegramReportDetail";
import UpdateReportTimeForm from "../_components/UpdateReportTimeForm";
import usePreferenceQuery from "../_hooks/usePreferenceQuery";
import usePreferenceMutation from "../_hooks/usePreferenceMutation";

export const Route = createFileRoute("/_private/preferences/$preferenceId/")({
  loader: async ({ params, context: { queryClient } }) => {
    const preference = await queryClient.ensureQueryData({
      queryKey: preferencesKeys.detail(params.preferenceId),
      queryFn: () => services.getPreference(params.preferenceId),
    });
    return { preference };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { preference: initialPreference } = Route.useLoaderData();
  const { preferenceId } = Route.useParams();
  const { data: preference } = usePreferenceQuery(preferenceId, initialPreference);
  const { t } = useTranslation();
  const formatter = useFormatter();
  const navigate = useNavigate();
  const { deletePreference, isDeleting } = usePreferenceMutation();

  const deleteConfirm = useConfirm({
    title: t("common.confirmDeleteTitle"),
    description: t("common.confirmDeleteDescription"),
    isPending: isDeleting,
  });

  const handleDelete = () => {
    deleteConfirm.confirm(async () => {
      await deletePreference(preferenceId);
      navigate({ to: "/preferences" });
    });
  };

  return (
    <div className="flex-1 flex flex-col gap-6">
      <Link
        to="/preferences"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("preferences.list.title")}
      </Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Bell className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight">
                {preference.type === "telegram_report"
                  ? t("preferences.type.telegram_report")
                  : preference.type}
              </h1>
              <Badge variant="info">
                {t(`preferences.type.${preference.type}`)}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span>
                {t("common.createdAt")}: {formatter.dateTime(preference.createdAt)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {preference.type === "telegram_report" && (
            <UpdateReportTimeForm language={preference.value.language} reportTime={preference.value.reportTime} />
          )}
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t("common.delete")}
          </Button>
        </div>
      </div>

      {preference.type === "telegram_report" && (
        <TelegramReportDetail value={preference.value} />
      )}

      {deleteConfirm.confirmDialog}
    </div>
  );
}
